import React, { useState } from "react";
import { useFormik } from "formik";
import {
  paragraphInitialValues,
  paragraphValidationSchema,
} from "./validationSchema";
import { SubSection } from "context/BlogContext";
import { FileEdit, Save, Trash2 } from "lucide-react";
import DeleteModal from "Common/DeleteModal";
import { createSelector } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";

interface CodeProps {
  sectionOrder: number;
  subSectionOrder: number;
  handleSaveSubSection: (
    sectionOrder: number,
    subSectionInfo: SubSection
  ) => void;
  handleEditSubSection: (sectionOrder: number, subSection: SubSection) => void;
  subSection: SubSection;
  handleDeleteSubSection: (subsectionOrder: number) => void;
}

const Code: React.FC<CodeProps> = ({
  sectionOrder,
  subSectionOrder,
  handleSaveSubSection,
  subSection,
  handleEditSubSection,
  handleDeleteSubSection,
}) => {
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [isCancel, setIsCancel] = useState(false);

  const selectDataList = createSelector(
    (state: any) => state.Blog,
    (state) => ({
      isLoading: state.isLoading,
    })
  );

  const { isLoading } = useSelector(selectDataList);

  const validation = useFormik({
    initialValues: paragraphInitialValues(subSection),
    validationSchema: paragraphValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      handleSaveSubSection(sectionOrder, {
        title: values.title,
        type: "code",
        attrs: { language: values.language, code: values.code },
        isEditing: false,
        order: subSectionOrder,
      });
    },
  });

  // Handle delete confirmation
  const handleDelete = () => {
    handleDeleteSubSection(subSection.order);
    setDeleteModal(false);
  };

  const handleCancelSubSection = () => {
    handleSaveSubSection(sectionOrder, {
      ...subSection,
      isEditing: false,
    });
    validation.resetForm();
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    validation.handleSubmit();
  };

  return (
    <div>
      <DeleteModal
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        onDelete={handleDelete}
        message="Are you sure you want to remove this subsection?"
      />
      {subSection.isEditing ? (
        <form>
          <div className="pt-5 cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12">
            <div className="xl:col-span-6">
              <label
                htmlFor={`subTitle`}
                className="inline-block mb-2 text-base font-medium"
              >
                Title
              </label>
              <input
                type="text"
                id="title"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Title"
                name="title"
                onChange={validation.handleChange} // Bind Formik onChange
                value={validation.values.title} // Bind Formik value
              />
            </div>
            <div className="pt-5">
              <label
                htmlFor={`language`}
                className="inline-block mb-2 text-base font-medium"
              >
                Language
              </label>
              <input
                type="text"
                id="language"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Language"
                name="language"
                onChange={validation.handleChange} // Bind Formik onChange
                value={validation.values.language} // Bind Formik value
              />
            </div>
            <div className="pt-5">
              <label
                htmlFor="text"
                className="inline-block mb-2 text-base font-medium"
              >
                Code
              </label>
              <textarea
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Enter paragraph"
                name="code"
                id="text"
                rows={3}
                value={validation.values.code} // Bind Formik value
                onChange={validation.handleChange} // Bind Formik onChange
              ></textarea>
              {validation.errors.code && validation.touched?.code && (
                <div className="text-red-500 text-sm pt-2">
                  {String(validation.errors.code)}
                </div>
              )}
            </div>
          </div>
        </form>
      ) : (
        <div className="w-full">
          {/* <h6 className="text-15 leading-[1] capitalize">{subSection.type}</h6> */}
          {subSection.title && (
            <h6 className="mb-2 text-15">{subSection.title}</h6>
          )}
          <pre
            className=""
            style={{
              backgroundColor: "#e1e1e1",
              color: "#000",
              padding: "20px",
              borderRadius: "5px",
              overflowX: "auto",
            }}
          >
            <code className="">{subSection.attrs?.code}</code>
          </pre>
        </div>
      )}
      <div className="flex justify-end gap-x-2.5 pt-5">
        {subSection.isEditing ? (
          <div className="flex gap-x-3">
            {isCancel && (
              <button
                type="button"
                className={`${
                  isLoading ? "cursor-not-allowed opacity-50" : ""
                } text-red-500`}
                onClick={() => handleCancelSubSection()}
                disabled={isLoading}
              >
                <div className="flex align-center">
                  <span className="ml-1">Cancel</span>
                </div>
              </button>
            )}
            <button
              type="button"
              className={`text-green-500 ${
                isLoading
                  ? "cursor-not-allowed opacity-50"
                  : "hover:bg-green-600 hover:text-white"
              } bg-green-100 btn focus:text-white focus:bg-green-600 focus:ring focus:ring-green-100 active:text-white active:bg-green-600 active:ring active:ring-green-100 dark:bg-green-500/20 dark:text-green-400 dark:hover:bg-green-500 dark:hover:text-white dark:focus:bg-green-500 dark:focus:text-white dark:active:bg-green-500 dark:active:text-white dark:ring-green-400/20`}
              onClick={(e) => handleSubmit(e)}
              disabled={isLoading}
            >
              <div className="flex align-center">
                <Save className="size-4"></Save>
                <span className="ml-1">Save</span>
              </div>
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              className={`text-custom-500 ${
                isLoading
                  ? "cursor-not-allowed opacity-50"
                  : " hover:bg-custom-600 hover:text-white"
              } btn bg-custom-100 focus:text-white focus:bg-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:ring active:ring-custom-100 dark:bg-custom-500/20 dark:text-custom-500 dark:hover:bg-custom-500 dark:hover:text-white dark:focus:bg-custom-500 dark:focus:text-white dark:active:bg-custom-500 dark:active:text-white dark:ring-custom-400/20`}
              onClick={() => {
                handleEditSubSection(sectionOrder, subSection);
                setIsCancel(true);
              }}
              disabled={isLoading}
            >
              <div className="flex align-center">
                <FileEdit className="size-4"></FileEdit>
                <span className="ml-1">Edit</span>
              </div>
            </button>
          </>
        )}
        <button
          type="button"
          className={`${
            isLoading ? "cursor-not-allowed opacity-50" : ""
          } text-red-500`}
          onClick={() => setDeleteModal(true)}
          disabled={isLoading}
        >
          <div className="flex align-center">
            <Trash2 className="size-4 text-red-500"></Trash2>
            <span className="ml-1">Delete</span>
          </div>
        </button>
      </div>
    </div>
  );
};

export default Code;
